import { normalizeInstanceUrl } from '../instance/config.ts';
import { isVideoId } from '../links/routes.ts';

/**
 * DeArrow, as data — the community database of replacement titles and thumbnails (the same people
 * and the same server family as SponsorBlock), keyed by video. This file is the *vocabulary*: the
 * stored settings shape with its normalisers, and the two request URLs. Reading the wire format
 * and choosing which submission stands is `parse.ts`. Nothing here fetches or hashes (R1) — the
 * k-anonymity hash is the platform's job, exactly as it is for SponsorBlock.
 *
 * Two services, measured 2026-08-02 (both `access-control-allow-origin: *`, so direct calls):
 *
 * - **The branding API** — `GET {base}/api/branding/{4-hex-prefix}` answers a bucket keyed by
 *   `videoID` (~150 entries), so which video is on screen never leaves the browser. The bare
 *   `/{videoID}` path form does **not** exist: the path segment is always read as a hash prefix,
 *   and an 11-character id answers `400 "Hash prefix does not match format requirements."` — the
 *   per-video form is a `?videoID=` query, which Visious never sends.
 * - **The thumbnail service** — `GET {thumbBase}/api/v1/getThumbnail?videoID={id}&time={sec}`
 *   answers `200 image/webp` (~16 KB, `cache-control: public`). Unlike the branding lookup it is
 *   sent the **plain video id** — an image fetch cannot be k-anonymised — which is why replacing
 *   thumbnails is its own switch under the enable switch.
 */

export interface DeArrowSettings {
  /** Off by default: enabling it sends (hashed) lookups to a third-party service. */
  readonly enabled: boolean;
  /**
   * Whether community thumbnails replace the originals too — titles alone otherwise. Its own
   * switch because the thumbnail service is sent the plain video id (see above), a different
   * privacy statement from the hashed title lookup.
   */
  readonly replaceThumbnails: boolean;
  /** A self-hosted branding server's address; `null` is the official one. */
  readonly apiUrl: string | null;
  /** A self-hosted thumbnail generator's address; `null` is the official one. */
  readonly thumbnailApiUrl: string | null;
}

export const DEARROW_DEFAULT_API_URL = 'https://sponsor.ajay.app';
export const DEARROW_DEFAULT_THUMBNAIL_API_URL = 'https://dearrow-thumb.ajay.app';

export const DEFAULT_DEARROW_SETTINGS: DeArrowSettings = {
  enabled: false,
  // On once the integration is on: community thumbnails are half of what DeArrow is, and the
  // switch below is the reader's way to keep the plain-id fetches from happening.
  replaceThumbnails: true,
  apiUrl: null,
  thumbnailApiUrl: null,
};

/**
 * A stored service address → a canonical base URL or `null` (the official service). Through the
 * instance normaliser for SponsorBlock's reason: these servers are addressed exactly like an
 * Invidious one — scheme, host, optional base path, nothing after it.
 */
export function normalizeDeArrowApiUrl(input: unknown): string | null {
  if (typeof input !== 'string' || input.trim() === '') return null;
  const result = normalizeInstanceUrl(input);
  return result.ok ? result.value : null;
}

/** Total (R4): the input is a stored blob or a hand-edited one. */
export function normalizeDeArrowSettings(input: unknown): DeArrowSettings {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return DEFAULT_DEARROW_SETTINGS;
  }
  const record = input as Readonly<Record<string, unknown>>;
  return {
    enabled: record['enabled'] === true,
    replaceThumbnails: record['replaceThumbnails'] !== false,
    apiUrl: normalizeDeArrowApiUrl(record['apiUrl']),
    thumbnailApiUrl: normalizeDeArrowApiUrl(record['thumbnailApiUrl']),
  };
}

/** The k-anonymity lookup URL, or `null` for a prefix that is not one (the caller's hash failed). */
export function deArrowRequestUrl(settings: DeArrowSettings, hashPrefix: string): string | null {
  if (!/^[0-9a-f]{4,32}$/.test(hashPrefix)) return null;
  const base = settings.apiUrl ?? DEARROW_DEFAULT_API_URL;
  return `${base}/api/branding/${hashPrefix}`;
}

/**
 * The generated thumbnail's URL. The `time` is the *chosen submission's* timestamp rather than
 * omitted — the service resolves the best submission itself when `time` is absent, but pinning it
 * keeps the picture the one `parse.ts` selected and makes the URL cacheable per choice.
 */
export function deArrowThumbnailUrl(
  settings: DeArrowSettings,
  videoId: string,
  timestampSec: number,
): string | null {
  if (!isVideoId(videoId)) return null;
  if (!Number.isFinite(timestampSec) || timestampSec < 0) return null;
  const base = settings.thumbnailApiUrl ?? DEARROW_DEFAULT_THUMBNAIL_API_URL;
  return `${base}/api/v1/getThumbnail?videoID=${encodeURIComponent(videoId)}&time=${timestampSec}`;
}
